import { useState, useEffect, useRef } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Trash2, 
  X, 
  PackagePlus, 
  Search,
  AlertCircle,
  Loader2,
  Check,
  ChevronDown,
  Eye,
  Filter,
  ListPlus,
  Printer
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
import { BarcodeStartPositionModal } from './BarcodeStartPositionModal';
import { stripItemIdPrefix } from '../../lib/itemId';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  product_id: '',
  quantity: '',
  tax_id: '',
  purchase_rate: '',
  sale_rate: '',
  display_rate: '',
  vendor_id: '',
  firm_id: '',
  invoice_date: '',
  invoice_number: '',
  payment_term_id: '',
  generate_items: true
};

export const Inward = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [barcodePrintEntryIds, setBarcodePrintEntryIds] = useState([]);
  const [showMultipleBarcodeModal, setShowMultipleBarcodeModal] = useState(false);
  const [selectedBarcodeEntryIds, setSelectedBarcodeEntryIds] = useState([]);
  const [barcodeSelectionSearch, setBarcodeSelectionSearch] = useState('');
  const [printingBarcodes, setPrintingBarcodes] = useState(false);
  const [printingQueueItems, setPrintingQueueItems] = useState([]);
  const [showPrintingQueueModal, setShowPrintingQueueModal] = useState(false);
  const [printingQueueLoading, setPrintingQueueLoading] = useState(false);
  const [printingQueueError, setPrintingQueueError] = useState('');
  const [queueSavingEntryId, setQueueSavingEntryId] = useState('');
  const [pendingQueuePrintItems, setPendingQueuePrintItems] = useState([]);
  
  // List filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterVendorId, setFilterVendorId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ dateFrom: '', dateTo: '', productId: '', company: '', vendorId: '', status: '' });

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dropdownRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, productsRes, taxesRes, vendorsRes, companiesRes, ptRes, queueRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inward`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/inward/printing-queue`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setEntries(entriesRes.data);
      setProducts(productsRes.data);
      setTaxes(taxesRes.data);
      setVendors(vendorsRes.data);
      setCompanies(companiesRes.data);
      setPaymentTerms(ptRes.data);
      setPrintingQueueItems(queueRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setFormData(prev => ({ ...prev, product_id: product.id }));
    setProductSearch(product.name);
    setShowProductDropdown(false);
  };

  const filteredProducts = products.filter(p =>
    p.active && (
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.product_code?.toLowerCase().includes(productSearch.toLowerCase())
    )
  );

  const openAddModal = () => {
    setFormData(initialFormData);
    setSelectedProduct(null);
    setProductSearch('');
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.product_id) {
      setError('Please select a product');
      return;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setError('Please enter a valid quantity');
      return;
    }
    if (!formData.purchase_rate || parseFloat(formData.purchase_rate) <= 0) {
      setError('Please enter a valid purchase rate');
      return;
    }
    if (!formData.sale_rate || parseFloat(formData.sale_rate) <= 0) {
      setError('Please enter a valid sale rate');
      return;
    }
    if (!formData.display_rate || parseFloat(formData.display_rate) <= 0) {
      setError('Please enter a valid display rate');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        product_id: formData.product_id,
        quantity: parseFloat(formData.quantity),
        tax_id: formData.tax_id || null,
        purchase_rate: parseFloat(formData.purchase_rate),
        sale_rate: parseFloat(formData.sale_rate),
        display_rate: parseFloat(formData.display_rate),
        vendor_id: formData.vendor_id || null,
        firm_id: formData.firm_id || null,
        invoice_date: formData.invoice_date || null,
        invoice_number: formData.invoice_number || null,
        payment_term_id: formData.payment_term_id || null,
        generate_items: formData.generate_items
      };

      await axios.post(`${BACKEND_URL}/api/inward`, payload, getAuthHeaders());
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/inward/${entryId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrintBarcodes = async (entryIds, startPosition = 1) => {
    const ids = Array.isArray(entryIds) ? entryIds : [entryIds];
    if (!ids.length) return;
    setPrintingBarcodes(true);
    const printWin = window.open('', '_blank', 'width=600,height=400');
    if (!printWin) {
      setPrintingBarcodes(false);
      alert('Please allow popups to print QR codes');
      return;
    }
    printWin.document.write('<!DOCTYPE html><html><head><title>Preparing QR Codes</title></head><body style="font-family:Arial,sans-serif;padding:24px">Preparing QR codes…</body></html>');
    printWin.document.close();
    try {
      const details = [];
      // Load in small batches so selecting all does not overwhelm the API.
      for (let index = 0; index < ids.length; index += 8) {
        const responses = await Promise.all(
          ids.slice(index, index + 8).map(entryId =>
            axios.get(`${BACKEND_URL}/api/inward/${entryId}`, getAuthHeaders())
          )
        );
        details.push(...responses.map(response => response.data));
      }
      const items = details.flatMap(detail => detail.items || []);
      if (!items.length) { printWin.close(); alert('No items to print'); return; }
      const printTitle = details.length === 1
        ? details[0].product_name || details[0].batch_number
        : `${details.length} Inward Batches`;

      const QR_CSS = `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page { margin: 0; }
        body { width: 83mm; font-family: Arial, sans-serif; background: #fff; }
        .grid {
          width: 83mm;
          margin-left: 11mm;
          margin-top: 0;
          display: grid;
          grid-template-columns: repeat(5, 16mm);
          column-gap: 0;
          row-gap: 5mm;
          padding: 0 1.5mm;
        }
        .qr-card {
          width: 16mm; height: 16mm;
          display: flex; align-items: center; justify-content: center;
          page-break-inside: avoid;
        }
        .qr-content {
          width: 16mm; height: 13.2mm;
          display: inline-flex; flex-direction: column;
          align-items: center; justify-content: center;
        }
        .qr-img { width: 10mm; height: 10mm; display: block; flex: none; }
        .code-text {
          width: 12mm; height: 2.2mm; line-height: 2.2mm;
          font-size: 6pt; font-family: monospace; margin-top: 1mm;
          text-align: center; white-space: nowrap; overflow: hidden;
        }
        @media print { body { -webkit-print-color-adjust: exact; } }
      `;
      printWin.document.open();
      printWin.document.write(`<!DOCTYPE html><html><head><title>QR Codes - ${printTitle}</title><style>${QR_CSS}</style></head><body><div class="grid">`);

      for (let slot = 1; slot < startPosition; slot += 1) {
        const emptyCard = printWin.document.createElement('div');
        emptyCard.className = 'qr-card';
        printWin.document.querySelector('.grid').appendChild(emptyCard);
      }

      for (const item of items) {
        try {
          const barcodeValue = stripItemIdPrefix(item.item_id);
          if (!barcodeValue) continue;
          const dataUrl = await QRCode.toDataURL(barcodeValue, { width: 120, margin: 1, errorCorrectionLevel: 'M' });
          const card = printWin.document.createElement('div');
          card.className = 'qr-card';
          card.innerHTML = `<div class="qr-content"><img class="qr-img" src="${dataUrl}" alt="QR"/><div class="code-text">${barcodeValue}</div></div>`;
          printWin.document.querySelector('.grid').appendChild(card);
        } catch { /* skip */ }
      }

      printWin.document.write('</div></body></html>');
      printWin.document.close();
      setTimeout(() => { printWin.print(); }, 400);
    } catch { printWin.close(); alert('Failed to load items for printing'); }
    finally { setPrintingBarcodes(false); }
  };

  const fetchPrintingQueue = async () => {
    setPrintingQueueLoading(true);
    setPrintingQueueError('');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inward/printing-queue`, getAuthHeaders());
      setPrintingQueueItems(response.data || []);
    } catch (err) {
      setPrintingQueueError(err.response?.data?.detail || 'Failed to load the printing queue');
    } finally {
      setPrintingQueueLoading(false);
    }
  };

  const openPrintingQueue = () => {
    setShowPrintingQueueModal(true);
    fetchPrintingQueue();
  };

  const addInwardToPrintingQueue = async (entry) => {
    setQueueSavingEntryId(entry.id);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/inward/printing-queue/inwards/${entry.id}`, {}, getAuthHeaders());
      await fetchPrintingQueue();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add inward items to the printing queue');
    } finally {
      setQueueSavingEntryId('');
    }
  };

  const removePrintingQueueItem = async (itemDbId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/inward/printing-queue/items/${itemDbId}`, getAuthHeaders());
      setPrintingQueueItems(current => current.filter(item => item.item_db_id !== itemDbId));
    } catch (err) {
      setPrintingQueueError(err.response?.data?.detail || 'Failed to remove item from the printing queue');
    }
  };

  const clearPrintingQueue = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/inward/printing-queue`, getAuthHeaders());
      setPrintingQueueItems([]);
    } catch (err) {
      setPrintingQueueError(err.response?.data?.detail || 'Failed to clear the printing queue');
    }
  };

  const handlePrintQueuedItems = async (items, startPosition = 1) => {
    if (!items.length) return;
    setPrintingBarcodes(true);
    const printWin = window.open('', '_blank', 'width=600,height=400');
    if (!printWin) {
      setPrintingBarcodes(false);
      alert('Please allow popups to print QR codes');
      return;
    }
    printWin.document.write('<!DOCTYPE html><html><head><title>Preparing QR Codes</title></head><body style="font-family:Arial,sans-serif;padding:24px">Preparing QR codes…</body></html>');
    printWin.document.close();
    try {
      const QR_CSS = `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page { margin: 0; }
        body { width: 83mm; font-family: Arial, sans-serif; background: #fff; }
        .grid {
          width: 83mm;
          margin-left: 11mm;
          margin-top: 0;
          display: grid;
          grid-template-columns: repeat(5, 16mm);
          column-gap: 0;
          row-gap: 5mm;
          padding: 0 1.5mm;
        }
        .qr-card {
          width: 16mm; height: 16mm;
          display: flex; align-items: center; justify-content: center;
          page-break-inside: avoid;
        }
        .qr-content {
          width: 16mm; height: 13.2mm;
          display: inline-flex; flex-direction: column;
          align-items: center; justify-content: center;
        }
        .qr-img { width: 10mm; height: 10mm; display: block; flex: none; }
        .code-text {
          width: 12mm; height: 2.2mm; line-height: 2.2mm;
          font-size: 6pt; font-family: monospace; margin-top: 1mm;
          text-align: center; white-space: nowrap; overflow: hidden;
        }
        @media print { body { -webkit-print-color-adjust: exact; } }
      `;
      printWin.document.open();
      printWin.document.write(`<!DOCTYPE html><html><head><title>QR Codes - Printing Queue</title><style>${QR_CSS}</style></head><body><div class="grid">`);
      for (let slot = 1; slot < startPosition; slot += 1) {
        const emptyCard = printWin.document.createElement('div');
        emptyCard.className = 'qr-card';
        printWin.document.querySelector('.grid').appendChild(emptyCard);
      }
      for (const item of items) {
        const barcodeValue = stripItemIdPrefix(item.item_id);
        if (!barcodeValue) continue;
        const dataUrl = await QRCode.toDataURL(barcodeValue, { width: 120, margin: 1, errorCorrectionLevel: 'M' });
        const card = printWin.document.createElement('div');
        card.className = 'qr-card';
        card.innerHTML = `<div class="qr-content"><img class="qr-img" src="${dataUrl}" alt="QR"/><div class="code-text">${barcodeValue}</div></div>`;
        printWin.document.querySelector('.grid').appendChild(card);
      }
      printWin.document.write('</div></body></html>');
      printWin.document.close();
      await axios.delete(`${BACKEND_URL}/api/inward/printing-queue`, getAuthHeaders());
      setPrintingQueueItems([]);
      setShowPrintingQueueModal(false);
      setTimeout(() => { printWin.print(); }, 400);
    } catch (err) {
      printWin.close();
      setPrintingQueueError(err.response?.data?.detail || 'Failed to print the queue');
      setShowPrintingQueueModal(true);
    } finally {
      setPrintingBarcodes(false);
    }
  };

  const companyNames = [...new Set(entries.map(e => e.firm_name).filter(Boolean))].sort();

  const applyFilters = () => {
    setAppliedFilters({ dateFrom: filterDateFrom, dateTo: filterDateTo, productId: filterProductId, company: filterCompany, vendorId: filterVendorId, status: filterStatus });
  };
  const clearFilters = () => {
    setFilterDateFrom(''); setFilterDateTo(''); setFilterProductId(''); setFilterCompany(''); setFilterVendorId(''); setFilterStatus('');
    setAppliedFilters({ dateFrom: '', dateTo: '', productId: '', company: '', vendorId: '', status: '' });
  };
  const hasActiveFilters = appliedFilters.dateFrom || appliedFilters.dateTo || appliedFilters.productId || appliedFilters.company || appliedFilters.vendorId || appliedFilters.status;

  const normalize = (str) => (str || '').toLowerCase().replace(/[\s\-\/\\.,:;_()]+/g, '');

  const filteredEntries = (() => {
    let result = entries;
    // Text search
    if (searchQuery.trim()) {
      const q = normalize(searchQuery);
      result = result.filter(e =>
        normalize(e.product_name).includes(q) ||
        normalize(e.product_code).includes(q) ||
        normalize(e.batch_number).includes(q) ||
        normalize(e.vendor_name).includes(q) ||
        normalize(e.firm_name).includes(q)
      );
    }
    if (appliedFilters.dateFrom) {
      result = result.filter(e => e.created_at >= appliedFilters.dateFrom);
    }
    if (appliedFilters.dateTo) {
      result = result.filter(e => e.created_at <= appliedFilters.dateTo + 'T23:59:59');
    }
    if (appliedFilters.productId) {
      result = result.filter(e => e.product_id === appliedFilters.productId);
    }
    if (appliedFilters.company) {
      result = result.filter(e => e.firm_name === appliedFilters.company);
    }
    if (appliedFilters.vendorId) {
      result = result.filter(e => e.vendor_id === appliedFilters.vendorId);
    }
    if (appliedFilters.status) {
      result = result.filter(e => (e.batch_status || 'Pending') === appliedFilters.status);
    }
    return result;
  })();

  const printableEntries = entries.filter(entry => Number(entry.items_count || 0) > 0);
  const visiblePrintableEntries = printableEntries.filter(entry => {
    if (!barcodeSelectionSearch.trim()) return true;
    const query = normalize(barcodeSelectionSearch);
    return normalize(entry.product_name).includes(query) ||
      normalize(entry.product_code).includes(query) ||
      normalize(entry.batch_number).includes(query) ||
      normalize(entry.vendor_name).includes(query) ||
      normalize(entry.firm_name).includes(query);
  });
  const selectedBarcodeCount = printableEntries
    .filter(entry => selectedBarcodeEntryIds.includes(entry.id))
    .reduce((total, entry) => total + Number(entry.items_count || 0), 0);

  const openMultipleBarcodeModal = () => {
    setSelectedBarcodeEntryIds([]);
    setBarcodeSelectionSearch('');
    setShowMultipleBarcodeModal(true);
  };

  const toggleBarcodeEntry = (entryId) => {
    setSelectedBarcodeEntryIds(current =>
      current.includes(entryId)
        ? current.filter(id => id !== entryId)
        : [...current, entryId]
    );
  };

  const selectAllPrintableEntries = () => {
    const allIds = printableEntries.map(entry => entry.id);
    setSelectedBarcodeEntryIds(current =>
      current.length === allIds.length ? [] : allIds
    );
  };

  return (
    <div data-testid="inward-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inward</h1>
          <p className="text-slate-600 mt-1">Add stock entries for products</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={openPrintingQueue}
            data-testid="printing-queue-btn"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white text-slate-700 font-bold tracking-wider uppercase px-5 py-3 rounded-none hover:border-orange-500 hover:text-orange-600 transition-colors"
          >
            <ListPlus className="h-5 w-5" />
            Printing Queue
            {printingQueueItems.length > 0 && (
              <span className="inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[11px] text-white">
                {printingQueueItems.length}
              </span>
            )}
          </button>
          <button
            onClick={openMultipleBarcodeModal}
            disabled={!printableEntries.length || printingBarcodes}
            data-testid="print-multiple-barcodes-btn"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white text-slate-700 font-bold tracking-wider uppercase px-5 py-3 rounded-none hover:border-orange-500 hover:text-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {printingBarcodes ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
            Print Multiple QRs
          </button>
          {perms.canAdd && (
            <button
              onClick={openAddModal}
              data-testid="add-inward-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add Inward Entry
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by product, batch, vendor, company..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors" />
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>
          {hasActiveFilters && (
            <button onClick={clearFilters} data-testid="clear-filters-btn"
              className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 items-end">
          {/* Date From */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">From Date</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              data-testid="filter-date-from"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Date To */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">To Date</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              data-testid="filter-date-to"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Product */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Product</label>
            <ProductAutocomplete
              value={filterProductId}
              onChange={(pid) => setFilterProductId(pid)}
              products={products}
              testId="filter-product"
              placeholder="Type product..."
            />
          </div>
          {/* Company */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
              data-testid="filter-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All</option>
              {companyNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          {/* Vendor */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label>
            <ProductAutocomplete
              value={filterVendorId}
              onChange={(vid) => setFilterVendorId(vid)}
              products={vendors}
              testId="filter-vendor"
              placeholder="Type vendor..."
            />
          </div>
          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Status</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              data-testid="filter-status"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All</option>
              <option value="Complete">Complete</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          {/* Apply */}
          <div>
            <button onClick={applyFilters} data-testid="apply-filters-btn"
              className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <PackagePlus className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">{entries.length === 0 ? 'No inward entries yet' : 'No entries match filters'}</h3>
            <p className="text-slate-500 mb-6">{entries.length === 0 ? 'Start by adding your first stock entry' : 'Try adjusting your filters'}</p>
            {entries.length === 0 && (
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Entry
            </button>
            )}
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Purchase Bill Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Inward Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Product</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Qty</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Items</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Batch Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`inward-row-${entry.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div className="text-slate-600 text-sm">{formatDate(entry.invoice_date)}</div>
                <div className="col-span-2">
                  <div className="text-sm text-slate-700">{formatDate(entry.created_at)}</div>
                </div>
                <div className="col-span-2">
                  <div className="font-medium text-slate-900">{entry.product_name}</div>
                  {entry.product_code && (
                    <div className="text-xs text-slate-500 font-mono">{entry.product_code}</div>
                  )}
                </div>
                <div className="text-slate-600 text-sm">{entry.firm_name || '-'}</div>
                <div className="text-slate-600 text-sm">{entry.vendor_name || '-'}</div>
                <div className="font-medium text-slate-900">{entry.quantity}</div>
                <div className="text-sm">
                  {entry.items_count > 0 ? (
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">{entry.items_count}</span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
                    entry.batch_status === 'Complete' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`} data-testid={`batch-status-${entry.id}`}>
                    {entry.batch_status || 'Pending'}
                  </span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <button
                    onClick={() => navigate(`/admin/inward/${entry.id}`)}
                    data-testid={`view-btn-${entry.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {entry.items_count > 0 && (
                    <button
                      onClick={() => addInwardToPrintingQueue(entry)}
                      disabled={queueSavingEntryId === entry.id}
                      data-testid={`add-inward-printing-queue-${entry.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                      title="Add all items to Printing Queue"
                    >
                      {queueSavingEntryId === entry.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <ListPlus className="h-4 w-4" />}
                    </button>
                  )}
                  {entry.items_count > 0 && (
                    <button
                      onClick={() => setBarcodePrintEntryIds([entry.id])}
                      data-testid={`print-barcodes-btn-${entry.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                      title="Print Barcodes"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(entry.id)}
                    data-testid={`delete-btn-${entry.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <BarcodeStartPositionModal
        open={barcodePrintEntryIds.length > 0}
        onClose={() => setBarcodePrintEntryIds([])}
        onConfirm={(position) => {
          const entryIds = barcodePrintEntryIds;
          setBarcodePrintEntryIds([]);
          handlePrintBarcodes(entryIds, position);
        }}
      />

      <BarcodeStartPositionModal
        open={pendingQueuePrintItems.length > 0}
        onClose={() => setPendingQueuePrintItems([])}
        onConfirm={(position) => {
          const items = pendingQueuePrintItems;
          setPendingQueuePrintItems([]);
          handlePrintQueuedItems(items, position);
        }}
      />

      {showPrintingQueueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="printing-queue-title">
          <div className="bg-white rounded-sm w-full max-w-3xl shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 id="printing-queue-title" className="text-xl font-bold text-slate-900 uppercase tracking-tight">Printing Queue</h2>
                <p className="text-sm text-slate-500 mt-1">Items will print continuously in the five-per-row label format.</p>
              </div>
              <button type="button" onClick={() => setShowPrintingQueueModal(false)} className="p-2 text-slate-400 hover:text-slate-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {printingQueueError && (
              <div className="mx-6 mt-4 flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />{printingQueueError}
              </div>
            )}

            <div className="overflow-y-auto flex-1 divide-y divide-slate-100" data-testid="printing-queue-item-list">
              {printingQueueLoading ? (
                <div className="flex justify-center py-14"><Loader2 className="h-7 w-7 animate-spin text-orange-500" /></div>
              ) : printingQueueItems.length === 0 ? (
                <div className="px-6 py-14 text-center">
                  <ListPlus className="mx-auto mb-3 h-12 w-12 text-slate-300" />
                  <p className="font-medium text-slate-700">Printing Queue is empty</p>
                  <p className="mt-1 text-sm text-slate-500">Add individual items from Inward Details or a complete inward from the listing.</p>
                </div>
              ) : printingQueueItems.map((item, index) => (
                <div key={item.id} className="flex items-center gap-4 px-6 py-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-700">{index + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{item.product_name || 'Unnamed Product'}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span className="font-mono text-slate-700">{item.item_id || '-'}</span>
                      {item.asset_id && <span>Asset: {item.asset_id}</span>}
                      <span>Batch: {item.batch_number || '-'}</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => removePrintingQueueItem(item.item_db_id)}
                    data-testid={`remove-printing-queue-${item.item_db_id}`}
                    className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-500" title="Remove from Printing Queue">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-600"><span className="font-bold">{printingQueueItems.length}</span> QR{printingQueueItems.length === 1 ? '' : 's'} queued</div>
              <div className="flex items-center gap-3">
                {printingQueueItems.length > 0 && (
                  <button type="button" onClick={clearPrintingQueue}
                    className="px-4 py-2 border border-red-200 text-red-600 rounded-sm hover:bg-red-50">
                    Clear Queue
                  </button>
                )}
                <button type="button" onClick={() => setShowPrintingQueueModal(false)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-white">
                  Close
                </button>
                <button type="button" disabled={!printingQueueItems.length || printingBarcodes}
                  onClick={() => { setShowPrintingQueueModal(false); setPendingQueuePrintItems([...printingQueueItems]); }}
                  data-testid="print-printing-queue"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Printer className="h-4 w-4" /> Print Queue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Select multiple inward batches for one continuous QR print job */}
      {showMultipleBarcodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="multiple-qr-title">
          <div className="bg-white rounded-sm w-full max-w-3xl shadow-xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 id="multiple-qr-title" className="text-xl font-bold text-slate-900 uppercase tracking-tight">Print Multiple QRs</h2>
                <p className="text-sm text-slate-500 mt-1">Select inward batches to combine into one print job.</p>
              </div>
              <button type="button" onClick={() => setShowMultipleBarcodeModal(false)} className="p-2 text-slate-400 hover:text-slate-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-slate-200 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={barcodeSelectionSearch}
                  onChange={event => setBarcodeSelectionSearch(event.target.value)}
                  placeholder="Search product, batch, vendor or company..."
                  data-testid="multiple-qr-search"
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={selectAllPrintableEntries}
                  data-testid="select-all-inwards-for-qr"
                  className="text-sm font-semibold text-orange-600 hover:text-orange-700"
                >
                  {selectedBarcodeEntryIds.length === printableEntries.length ? 'Clear All' : 'Select All'}
                </button>
                <div className="text-sm text-slate-600">
                  <span className="font-semibold">{selectedBarcodeEntryIds.length}</span> batches · <span className="font-semibold">{selectedBarcodeCount}</span> QRs
                </div>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 divide-y divide-slate-100" data-testid="multiple-qr-entry-list">
              {visiblePrintableEntries.length === 0 ? (
                <div className="px-6 py-12 text-center text-slate-500">No inward batches with QR items found.</div>
              ) : visiblePrintableEntries.map(entry => {
                const selected = selectedBarcodeEntryIds.includes(entry.id);
                return (
                  <label key={entry.id} className={`flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-slate-50 ${selected ? 'bg-orange-50/60' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleBarcodeEntry(entry.id)}
                      data-testid={`multiple-qr-entry-${entry.id}`}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{entry.product_name || 'Unnamed Product'}</div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-mono">{entry.batch_number || '-'}</span>
                        <span>{entry.vendor_name || 'No vendor'}</span>
                        <span>{entry.firm_name || 'No company'}</span>
                      </div>
                    </div>
                    <span className="shrink-0 bg-slate-100 text-slate-700 px-2.5 py-1 rounded text-xs font-bold">
                      {entry.items_count} QRs
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" onClick={() => setShowMultipleBarcodeModal(false)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-white">
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedBarcodeEntryIds.length}
                onClick={() => {
                  const selectedIds = printableEntries
                    .filter(entry => selectedBarcodeEntryIds.includes(entry.id))
                    .map(entry => entry.id);
                  setShowMultipleBarcodeModal(false);
                  setBarcodePrintEntryIds(selectedIds);
                }}
                data-testid="continue-multiple-qr-print"
                className="inline-flex items-center gap-2 px-5 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="h-4 w-4" /> Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this entry? This will reduce the inventory quantity.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                Add Inward Entry
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Product Search - Full Width */}
              <div ref={dropdownRef} className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Product <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductDropdown(true);
                        if (!e.target.value) {
                          setSelectedProduct(null);
                          setFormData(prev => ({ ...prev, product_id: '' }));
                        }
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Search product by name or code..."
                      data-testid="input-product-search"
                      className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  
                  {showProductDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="px-4 py-3 text-slate-400 text-sm">No products found</div>
                      ) : (
                        filteredProducts.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleProductSelect(product)}
                            className={`w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between ${
                              selectedProduct?.id === product.id ? 'bg-orange-50' : ''
                            }`}
                          >
                            <div>
                              <div className="font-medium text-slate-900">{product.name}</div>
                              {product.product_code && (
                                <div className="text-xs text-slate-500 font-mono">{product.product_code}</div>
                              )}
                            </div>
                            {selectedProduct?.id === product.id && (
                              <Check className="h-4 w-4 text-orange-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 2-Column Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-quantity"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Tax */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax</label>
                  <select
                    name="tax_id"
                    value={formData.tax_id}
                    onChange={handleInputChange}
                    data-testid="input-tax"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Tax</option>
                    {taxes.filter(t => t.active).map(tax => (
                      <option key={tax.id} value={tax.id}>{tax.name} ({tax.percentage}%)</option>
                    ))}
                  </select>
                </div>

                {/* Vendor */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select Vendor</label>
                  <ProductAutocomplete value={formData.vendor_id} onChange={(id) => setFormData(prev => ({ ...prev, vendor_id: id }))} products={vendors.filter(v => v.active)} testId="input-vendor" placeholder="Type vendor name..." />
                </div>

                {/* Inward Firm (Company) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Select Inward Firm</label>
                  <select
                    name="firm_id"
                    value={formData.firm_id}
                    onChange={handleInputChange}
                    data-testid="input-firm"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Firm</option>
                    {companies.filter(c => c.active).map(company => (
                      <option key={company.id} value={company.id}>{company.company_name}</option>
                    ))}
                  </select>
                </div>

                {/* Invoice Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleInputChange}
                    data-testid="input-invoice-date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Invoice Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={formData.invoice_number}
                    onChange={handleInputChange}
                    placeholder="Enter invoice number"
                    data-testid="input-invoice-number"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Payment Terms */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
                  <select
                    name="payment_term_id"
                    value={formData.payment_term_id}
                    onChange={handleInputChange}
                    data-testid="input-payment-term"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  >
                    <option value="">Select Payment Term</option>
                    {paymentTerms.filter(pt => pt.active).map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Purchase Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Purchase Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="purchase_rate"
                    value={formData.purchase_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-purchase-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Sale Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Sale Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="sale_rate"
                    value={formData.sale_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-sale-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Display Rate */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Rate (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="display_rate"
                    value={formData.display_rate}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    data-testid="input-display-rate"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Generate Individual Items Toggle - Full Width */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm mt-4">
                <div>
                  <div className="text-sm font-medium text-slate-700">Generate Individual Items</div>
                  <div className="text-xs text-slate-500 mt-0.5">Creates a unique Item ID for each unit</div>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, generate_items: !prev.generate_items }))}
                  data-testid="toggle-generate-items"
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    formData.generate_items ? 'bg-orange-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    formData.generate_items ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
